/**
 * useAnalytics: use this in components to get track/identify/screen/reset.
 * Events are sent to Mixpanel (primary) and to PostHog when configured (pixels + feature flags).
 */

import { useCallback } from 'react'
import { Platform } from 'react-native'
import { mixpanelTrack, mixpanelIdentify, mixpanelReset } from './mixpanel.client'
import { getAnalyticsContext } from './analyticsContext.helper'
import { Mixpanel } from './mixpanelPlatform'
import { usePostHogClient } from './PostHogClientContext'
import { getWebAnalyticsConsent } from '../privacy/helpers/webAnalyticsConsent.helper'
import { WEB_ANALYTICS_CONSENT_DENIED } from '../privacy/privacyConsent.constants'
import type { AnalyticsEventProperties, AnalyticsScreenName } from './events'

const withContext = <T extends Record<string, unknown> | undefined>(props?: T) => ({
  ...getAnalyticsContext(),
  ...(props ?? {}),
})

export interface UseAnalyticsReturn {
  track: (event: string, properties?: AnalyticsEventProperties) => void
  identify: (distinctId: string, properties?: Record<string, unknown>) => void
  screen: (screenName: AnalyticsScreenName | string, properties?: AnalyticsEventProperties) => void
  reset: () => void
}

export function useAnalytics(): UseAnalyticsReturn {
  const posthog = usePostHogClient()

  const track = useCallback((event: string, properties?: AnalyticsEventProperties) => {
    if (getWebAnalyticsConsent() === WEB_ANALYTICS_CONSENT_DENIED) return
    const props = withContext(properties as Record<string, unknown> | undefined) as Record<string, unknown>
    if (Platform.OS === 'web') {
      mixpanelTrack(event, props)
    } else {
      Mixpanel.track(event, props)
    }
    if (posthog) posthog.capture(event, props)
  }, [posthog])

  const identify = useCallback((distinctId: string, properties?: Record<string, unknown>) => {
    if (getWebAnalyticsConsent() === WEB_ANALYTICS_CONSENT_DENIED) return
    const props = withContext(properties)
    if (Platform.OS === 'web') {
      mixpanelIdentify(distinctId, props)
    } else {
      Mixpanel.identify(distinctId)
      if (Object.keys(props).length > 0 && typeof Mixpanel.getPeople === 'function') {
        Mixpanel.getPeople().set(props)
      }
    }
    if (posthog) posthog.identify(distinctId, props)
  }, [posthog])

  const screen = useCallback((screenName: AnalyticsScreenName | string, properties?: AnalyticsEventProperties) => {
    if (getWebAnalyticsConsent() === WEB_ANALYTICS_CONSENT_DENIED) return
    const props = withContext({ ...(properties as Record<string, unknown>), screen: screenName }) as Record<string, unknown>
    if (Platform.OS === 'web') {
      mixpanelTrack('$pageview', { $current_url: screenName, ...props })
    } else {
      Mixpanel.track('$pageview', { $current_url: screenName, screen: screenName, ...props })
    }
    if (posthog) {
      if (posthog.screen) {
        posthog.screen(screenName, props)
      } else {
        posthog.capture('$pageview', { $current_url: screenName, screen: screenName, ...props })
      }
    }
  }, [posthog])

  const reset = useCallback(() => {
    if (getWebAnalyticsConsent() === WEB_ANALYTICS_CONSENT_DENIED) return
    if (Platform.OS === 'web') {
      mixpanelReset()
    } else {
      Mixpanel.reset()
    }
    if (posthog) posthog.reset()
  }, [posthog])

  return { track, identify, screen, reset }
}
